import axios from 'axios'
import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL } from '../api/endpoints'

interface Role {
  role?: string
}

interface User {
  role?: Role
}

interface AuthContextProps {
  authToken: string | null
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  user: User
  refreshauthToken: () => Promise<void>
  userPermissions: string[]
}

const AuthContext = createContext<AuthContextProps>({
  authToken: null,
  login: async () => {},
  logout: () => {},
  user: {},
  refreshauthToken: async () => {},
  userPermissions: [],
})

export const useAuth = () => {
  return useContext(AuthContext)
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [userPermissions, setUserPermissions] = useState<string[]>([])

  const [authToken, setauthToken] = useState<string | null>(
    sessionStorage.getItem('authToken')
  )

  const [refreshToken, setRefreshToken] = useState<string | null>(
    sessionStorage.getItem('refreshToken')
  )

  const [user, setUser] = useState<User>({})
  const navigate = useNavigate()

  useEffect(() => {
    // Optionally, you might want to refresh the token on app load if expired
    if (authToken && refreshToken) {
      refreshauthToken()
    }
  }, [authToken, refreshToken])

  const login = async (email: string, password: string) => {
    try {
      const response = await axios.post(`${BASE_URL}/login`, {
        email,
        password,
      })

      if (response?.status !== 200) {
        throw new Error('Login failed')
      }

      const {
        token,
        refreshToken: newRefreshToken,
        schoolFeatures,
      } = response.data.data
      sessionStorage.setItem('authToken', token)
      sessionStorage.setItem('refreshToken', newRefreshToken)
      const permissions = schoolFeatures?.permissions || []
      localStorage.setItem('userPermissions', JSON.stringify(permissions))
      setUserPermissions(permissions) // Update state with permissions

      setauthToken(token)
      setRefreshToken(newRefreshToken)
      setUser(response?.data?.data)
      navigate('/home')

      // Save user details to local storage as needed
      localStorage.setItem('userRole', response?.data?.data?.role?.role ?? '')
      localStorage.setItem('userEmail', response?.data?.data?.user?.email ?? '')
      localStorage.setItem(
        'userName',
        response?.data?.data?.basicInfo.firstName ?? ''
      )
      localStorage.setItem(
        'schoolName',
        response?.data?.data?.school?.name ?? ''
      )
    } catch (error) {
      console.error('Login failed', error)
      toast.error(
        'Oops! Incorrect email or password. Please double-check your credentials and try again.',
        { position: 'top-right' }
      )
    }
  }

  const refreshauthToken = async () => {
    if (!refreshToken) {
      return
    }
    try {
      const response = await axios.post(`${BASE_URL}/refresh-token`, {
        refreshToken: refreshToken,
      })

      if (response?.status !== 200) {
        throw new Error('Token refresh failed')
      }

      const {
        token,
        refreshToken: newRefreshToken,
        schoolFeatures,
      } = response.data.data
      sessionStorage.setItem('authToken', token)
      sessionStorage.setItem('refreshToken', newRefreshToken)
      const permissions = schoolFeatures?.permissions || []
      localStorage.setItem('userPermissions', JSON.stringify(permissions))
      setUserPermissions(permissions) // Update state with permissions

      setauthToken(token)
      setRefreshToken(newRefreshToken)
    } catch (error) {
      console.error('Token refresh failed', error)
      // Handle token refresh failure, e.g., log out the user
      logout()
    }
  }

  const logout = () => {
    // Clear the auth token and refresh token from state and local storage
    sessionStorage.removeItem('authToken')
    sessionStorage.removeItem('refreshToken')
    localStorage.removeItem('userRole')
    localStorage.removeItem('userEmail')
    localStorage.removeItem('userName')
    localStorage.removeItem('schoolName')
    localStorage.removeItem('SchoolIds')
    sessionStorage.removeItem('userPermissions')
    localStorage.removeItem('userPermissions')
    setauthToken(null)
    setRefreshToken(null)
    setUser({})
    navigate('/')
  }

  useEffect(() => {
    // Load permissions from local storage on component mount
    const storedPermissions = localStorage.getItem('userPermissions')
    if (storedPermissions) {
      setUserPermissions(JSON.parse(storedPermissions))
    }
  }, [])

  return (
    <AuthContext.Provider
      value={{
        authToken,
        login,
        logout,
        user,
        refreshauthToken,
        userPermissions,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
